import { useEffect, useState } from "react";
import { Loader2, FileCheck2, Save, CheckCircle2, FileUp, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { CHECKLIST_ITEMS, CHECKLIST_OPTIONS } from "@/components/tech/VisitReportForm";

const fileUrl = (reportId, fileId) => `${process.env.REACT_APP_BACKEND_URL}/api/visit-files/${reportId}/${fileId}`;

const ChecklistEditor = ({ reportId, checklist, onChange }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-3">
    {CHECKLIST_ITEMS.map((item) => (
      <div key={item.key} className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-zinc-700">{item.label}</span>
        <div className="flex gap-1">
          {CHECKLIST_OPTIONS.map((opt) => (
            <button key={opt.value} type="button" onClick={() => onChange(item.key, opt.value)}
              data-testid={`admin-checklist-${reportId}-${item.key}-${opt.value}`}
              className={`text-[10px] font-bold px-2 py-1 rounded-sm border transition-colors duration-150 ${checklist[item.key] === opt.value ? opt.tone : "bg-white text-zinc-500 border-zinc-300"}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const PhotoGrid = ({ report, removed, toggleRemove }) => (
  <div className="flex flex-wrap gap-2 mt-3">
    {report.photos.map((p) => {
      const isRemoved = removed.includes(p.file_id);
      return (
        <div key={p.file_id} className="relative">
          <img src={fileUrl(report.id, p.file_id)} alt="Visit"
            className={`w-20 h-20 object-cover rounded-sm border ${isRemoved ? "opacity-30 border-brand-red" : "border-zinc-200"}`} />
          <button type="button" onClick={() => toggleRemove(p.file_id)} data-testid={`admin-photo-remove-${report.id}-${p.file_id}`}
            className={`absolute -top-1.5 -right-1.5 rounded-full p-0.5 ${isRemoved ? "bg-zinc-500" : "bg-brand-red"} text-white`}
            aria-label={isRemoved ? "Keep photo" : "Remove photo"}>
            <X className="w-3 h-3" />
          </button>
        </div>
      );
    })}
  </div>
);

const InvoiceControls = ({ report, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("invoice", file);
    try {
      const r = await api.post(`/admin/visit-reports/${report.id}/invoice`, fd);
      toast.success(report.invoice ? "Invoice replaced" : "Invoice uploaded — customer notified");
      onUpdated(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/visit-reports/${report.id}/invoice`);
      toast.success("Invoice deleted");
      onUpdated({ ...report, invoice: null });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-zinc-100">
      {report.invoice && (
        <a href={fileUrl(report.id, report.invoice.file_id)} target="_blank" rel="noopener noreferrer"
          data-testid={`admin-invoice-view-${report.id}`}
          className="text-xs font-bold text-brand underline">{report.invoice.original_filename}</a>
      )}
      <label className={`text-xs font-bold px-3 py-1.5 rounded-sm border-2 border-brand text-brand hover:bg-brand hover:text-white cursor-pointer flex items-center gap-1 transition-colors duration-200 ${busy ? "opacity-50 pointer-events-none" : ""}`}>
        <FileUp className="w-3.5 h-3.5" /> {report.invoice ? "Replace invoice PDF" : "Upload invoice PDF"}
        <input data-testid={`admin-invoice-input-${report.id}`} type="file" accept="application/pdf" className="hidden" onChange={upload} />
      </label>
      {report.invoice && (
        <button type="button" onClick={remove} disabled={busy} data-testid={`admin-invoice-delete-${report.id}`}
          className="text-xs font-bold text-brand-red flex items-center gap-1 disabled:opacity-50">
          <Trash2 className="w-3.5 h-3.5" /> Delete invoice
        </button>
      )}
    </div>
  );
};

const ReportCard = ({ report, onUpdated }) => {
  const [notes, setNotes] = useState(report.notes);
  const [repairs, setRepairs] = useState(report.recommended_repairs);
  const [checklist, setChecklist] = useState(report.checklist);
  const [removed, setRemoved] = useState([]);
  const [busy, setBusy] = useState(false);
  const pending = report.status === "pending";

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/admin/visit-reports/${report.id}`, {
        notes, recommended_repairs: repairs, checklist, remove_photo_ids: removed,
      });
      toast.success("Report updated");
      setRemoved([]);
      onUpdated(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/visit-reports/${report.id}/approve`);
      toast.success("Report approved — customer notified");
      onUpdated(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li data-testid={`admin-visit-report-${report.id}`} className="py-4 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-brand-ink">
          {report.customer_name}
          {report.location ? <span className="text-zinc-500 font-normal"> · {report.location.label}, {report.location.address}</span> : ""}
        </p>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-sm uppercase tracking-wide ${pending ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`}>
          {pending ? "Pending approval" : "Approved"}
        </span>
      </div>
      <p className="text-xs text-zinc-500 mt-0.5">{report.date} · Technician: {report.technician_name}</p>
      <ChecklistEditor reportId={report.id} checklist={checklist} onChange={(k, v) => setChecklist({ ...checklist, [k]: v })} />
      <Textarea data-testid={`admin-visit-notes-${report.id}`} rows={2} placeholder="Notes / assessment" value={notes}
        onChange={(e) => setNotes(e.target.value)} className="rounded-sm mt-3 text-sm" />
      <Textarea data-testid={`admin-visit-repairs-${report.id}`} rows={2} placeholder="Recommended repairs" value={repairs}
        onChange={(e) => setRepairs(e.target.value)} className="rounded-sm mt-2 text-sm" />
      {report.photos.length > 0 && <PhotoGrid report={report} removed={removed} toggleRemove={(id) =>
        setRemoved((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))} />}
      <div className="flex flex-wrap gap-2 mt-3">
        <button type="button" onClick={save} disabled={busy} data-testid={`admin-visit-save-${report.id}`}
          className="border-2 border-brand text-brand hover:bg-brand hover:text-white disabled:opacity-50 font-bold px-4 py-2 rounded-sm text-xs flex items-center gap-1.5 transition-colors duration-200">
          <Save className="w-3.5 h-3.5" /> Save Changes
        </button>
        {pending && (
          <button type="button" onClick={approve} disabled={busy} data-testid={`admin-visit-approve-${report.id}`}
            className="bg-brand hover:bg-brand-dark disabled:opacity-50 text-white font-bold px-4 py-2 rounded-sm text-xs flex items-center gap-1.5 transition-colors duration-200">
            <CheckCircle2 className="w-3.5 h-3.5" /> Approve &amp; Publish to Customer
          </button>
        )}
      </div>
      <InvoiceControls report={report} onUpdated={onUpdated} />
    </li>
  );
};

export const AdminVisitReports = () => {
  const [reports, setReports] = useState(null);
  const [tab, setTab] = useState("pending");

  useEffect(() => {
    api.get("/admin/visit-reports").then((r) => setReports(r.data.reports)).catch(() => setReports([]));
  }, []);

  const onUpdated = (updated) => setReports((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  const shown = reports?.filter((r) => r.status === tab) || [];
  const pendingCount = reports?.filter((r) => r.status === "pending").length || 0;

  return (
    <div data-testid="admin-visit-reports-card" className="bg-white border border-zinc-200 p-6 rounded-sm mt-6">
      <h2 className="font-heading font-bold text-lg text-brand-ink mb-1 flex items-center gap-2">
        <FileCheck2 className="w-5 h-5 text-brand" /> Visit Reports (Admin)
      </h2>
      <p className="text-sm text-zinc-600 mb-4">Review technician reports, edit anything, then approve to publish to the customer's Service History.</p>
      <div className="flex gap-2 mb-4">
        {["pending", "approved"].map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} data-testid={`admin-visit-tab-${t}`}
            className={`text-xs font-bold px-4 py-2 rounded-sm uppercase tracking-wide transition-colors duration-200 ${tab === t ? "bg-brand text-white" : "bg-brand-subtle text-zinc-600"}`}>
            {t}{t === "pending" ? ` (${pendingCount})` : ""}
          </button>
        ))}
      </div>
      {reports === null && <Loader2 className="w-6 h-6 text-brand animate-spin" />}
      {reports !== null && shown.length === 0 && (
        <p data-testid="admin-visit-reports-empty" className="text-sm text-zinc-600">No {tab} reports.</p>
      )}
      {shown.length > 0 && (
        <ul className="divide-y divide-zinc-100">
          {shown.map((r) => <ReportCard key={r.id} report={r} onUpdated={onUpdated} />)}
        </ul>
      )}
    </div>
  );
};
